import React, { useEffect, useState, useMemo } from "react";
import Header from "../../../components/Header/Header";
import SearchableTable from "../../../components/Tables/SearchableTable";
import { SupplierColumns } from "../../../assets/Columns/SupplierColumns";
import CustomBtn from "../../../components/Buttons/CustomBtn";
import BodyWrapper from "../../../components/Wrapper/BodyWrapper";
import CreateSupplierModal from "../../../components/Modals/CreateSupplierModal";
import { useDispatch, useSelector } from "react-redux";
import { fetchSuppliers } from "../../../store/Slices/SupplierSlice";
import { useNavigate } from "react-router-dom";
import FetchingLoading from "../../../components/Loaders/FetchingLoading";
import EditSupplierModal from "../../../components/Modals/EditSupplierModal";
import DeleteModal from "../../../components/Modals/DeleteModal";
import { DeleteCompanyAPI } from "../../../ApiRequests";
import { ErrorToast, SuccessToast } from "../../../utils/ShowToast";
import ExportToExcelButton from "../../../utils/ExportToExcel";
import PrintButton from "../../../components/Buttons/PrintButton";
import AddDirectDiscountModal from "../../../components/Modals/AddDirectDiscountModal";
import AddPaymentModal from "../../../components/Modals/AddPaymentModal";
import { filterTableRowsBySearch } from "../../../utils/tableSearch";
import MetricCard from "../../../components/Cards/MetricCard";

const getAppBasePath = () =>
  Number(localStorage.getItem("role")) === 1 ? "/admin" : "/branch";

const Supplier = () => {
  const [SearchText, setSearchText] = useState("");
  const [OpenModal, setOpenModal] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const Suppliers = useSelector((state) => state.Suppliers);

  const [OpenEditModal, setOpenEditModal] = useState(false);
  const [OpenDeleteModal, setOpenDeleteModal] = useState(false);
  const [Selected, setSelected] = useState("");
  const [Loading, setLoading] = useState(false);
  const [OpenDiscountModal, setOpenDiscountModal] = useState(false);
  const [OpenPaymentModal, setOpenPaymentModal] = useState(false);

  useEffect(() => {
    dispatch(fetchSuppliers());
  }, [dispatch]);

  const enrichedSuppliersData = useMemo(() => {
    return Suppliers.data.map((supplier) => ({
      ...supplier,
      total: supplier.total || 0,
      paid: supplier.paid || 0,
      payable: supplier.payable || 0,
      discount: supplier.discount || 0,
    }));
  }, [Suppliers.data]);

  const summary = useMemo(() => {
    if (!Suppliers.data || Suppliers.data.length === 0) {
      return {
        total: 0,
        paid: 0,
        payable: 0,
      };
    }

    return Suppliers.data.reduce(
      (acc, supplier) => ({
        total: acc.total + (supplier.total || 0),
        paid: acc.paid + (supplier.paid || 0),
        payable: acc.payable + (supplier.payable || 0),
      }),
      {
        total: 0,
        paid: 0,
        payable: 0,
      }
    );
  }, [Suppliers.data]);

  const filteredSuppliers = useMemo(
    () =>
      filterTableRowsBySearch(enrichedSuppliersData, SearchText, SupplierColumns),
    [enrichedSuppliersData, SearchText]
  );

  return (
    <BodyWrapper>
      <Header title="Companies" desc="Overview of your companies management">
        <CustomBtn
          title={"Add New Company"}
          onClick={() => {
            setOpenModal(true);
          }}
        />
        <CustomBtn
          title={"Discount log"}
          onClick={() => navigate(`${getAppBasePath()}/discount-log`)}
        />
      </Header>
      <div className="flex justify-end gap-x-2">
        <PrintButton
          onClick={() => {
            navigate(`${getAppBasePath()}/companies-report`);
          }}
          title="Company Report"
        />
        <ExportToExcelButton data={filteredSuppliers} fileName={"Suppliers"} />
      </div>
      <div className="w-full mt-10 flex flex-col items-center">
        {Suppliers.loading ? (
          <div className="flex flex-1 justify-center items-center">
            <FetchingLoading />
          </div>
        ) : (
          enrichedSuppliersData.length !== 0 && (
            <div className="w-full">
              <SearchableTable
                setOpenEditModal={setOpenEditModal}
                setOpenDeleteModal={setOpenDeleteModal}
                setSelected={setSelected}
                onAddDiscount={(row) => {
                  setSelected(row);
                  setOpenDiscountModal(true);
                }}
                onViewDiscountLog={(row) => {
                  navigate(
                    `${getAppBasePath()}/discount-log/company/${row._id}`
                  );
                }}
                onViewLedger={(row) => {
                  navigate(`${getAppBasePath()}/ledger`, {
                    state: { company: row },
                  });
                }}
                onAddPayment={(row) => {
                  setSelected(row);
                  setOpenPaymentModal(true);
                }}
                onViewPayments={(row) => {
                  navigate(`${getAppBasePath()}/company-payments/${row._id}`);
                }}
                SearchPlaceholder={"Search companies..."}
                SearchText={SearchText}
                setSearchText={setSearchText}
                CurrentData={enrichedSuppliersData}
                Columns={SupplierColumns}
              />
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                <MetricCard
                  title="Total Amount"
                  value={`Rs ${summary.total.toLocaleString("en-PK")}`}
                  subtitle="Total billed by all companies"
                  tone="neutral"
                />
                <MetricCard
                  title="Total Paid"
                  value={`Rs ${summary.paid.toLocaleString("en-PK")}`}
                  subtitle="Total payments already made"
                  tone="success"
                />
                <MetricCard
                  title="Total Payable"
                  value={`Rs ${summary.payable.toLocaleString("en-PK")}`}
                  subtitle="Outstanding dues to companies"
                  tone="danger"
                />
              </div>
            </div>
          )
        )}
      </div>
      {OpenModal && (
        <CreateSupplierModal Open={OpenModal} setOpen={setOpenModal} />
      )}

      {OpenEditModal && (
        <EditSupplierModal
          open={OpenEditModal}
          setOpen={setOpenEditModal}
          supplierData={Selected}
        />
      )}
      {OpenPaymentModal && Selected && (
        <AddPaymentModal
          Open={OpenPaymentModal}
          setOpen={(open) => {
            setOpenPaymentModal(open);
            if (!open) setSelected("");
          }}
          presetEntityType="company"
          presetEntity={Selected}
        />
      )}
      {OpenDiscountModal && Selected && (
        <AddDirectDiscountModal
          Open={OpenDiscountModal}
          setOpen={(open) => {
            setOpenDiscountModal(open);
            if (!open) setSelected("");
          }}
          userType={2}
          entity={Selected}
          onSuccess={() => dispatch(fetchSuppliers())}
        />
      )}
      {OpenDeleteModal && (
        <DeleteModal
          Open={OpenDeleteModal}
          setOpen={setOpenDeleteModal}
          Loading={Loading}
          onSubmit={async () => {
            setLoading(true);
            try {
              const response = await DeleteCompanyAPI(Selected._id);
              if (response.data.success) {
                setOpenDeleteModal(false);
                SuccessToast("Company delete successfully");
                dispatch(fetchSuppliers());
              } else {
                ErrorToast(response.data.error?.msg);
              }
            } catch (err) {
              console.log(err);
              ErrorToast("Failed to delete customer!");
            }
            setLoading(false);
          }}
        />
      )}
    </BodyWrapper>
  );
};

export default Supplier;
